import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Play, Copy, RefreshCw, Save, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Link, Navigate } from "react-router-dom";
import { useSeason } from "@/context/SeasonContext";
import { useAuth } from "@/context/AuthContext";

const emptyRule = {
  season_id: "", category_id: "", birth_year_from: null, birth_year_to: null,
  birth_date_from: null, birth_date_to: null, is_primary: true, priority: 0, is_active: true,
};

export default function SeasonsSettings() {
  const { user } = useAuth();
  const { seasons, reload, activeSeason } = useSeason();
  const [selectedId, setSelectedId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState([]);
  const [preview, setPreview] = useState(null);
  const [seasonForm, setSeasonForm] = useState({ open: false, mode: "new", data: { name: "", start_date: "", end_date: "", is_active: false } });
  const [catForm, setCatForm] = useState({ open: false, mode: "new", data: { code: "", male_name: "", female_name: "", neutral_name: "", category_type: "age", sort_order: 0, is_active: true } });
  const [ruleForm, setRuleForm] = useState({ open: false, mode: "new", data: emptyRule });
  const [duplicateFrom, setDuplicateFrom] = useState("");

  useEffect(() => {
    if (!selectedId && seasons.length) {
      setSelectedId(activeSeason?.id || seasons[0].id);
    }
  }, [seasons, activeSeason, selectedId]);

  const loadCategoriesAndRules = async () => {
    const [c, r] = await Promise.all([
      api.get("/categories"),
      selectedId ? api.get(`/season-rules?season_id=${selectedId}`) : Promise.resolve({ data: [] }),
    ]);
    setCategories(c.data || []);
    setRules(r.data || []);
  };

  useEffect(() => { loadCategoriesAndRules(); /* eslint-disable-next-line */ }, [selectedId]);

  const catsById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const rulesByCat = useMemo(() => {
    const m = {};
    rules.forEach((r) => { (m[r.category_id] ||= []).push(r); });
    return m;
  }, [rules]);

  // Season CRUD
  const openNewSeason = () => setSeasonForm({ open: true, mode: "new", data: { name: "", start_date: "", end_date: "", is_active: false } });
  const openEditSeason = (s) => setSeasonForm({ open: true, mode: "edit", data: { ...s } });
  const saveSeason = async () => {
    try {
      if (seasonForm.mode === "new") await api.post("/seasons", seasonForm.data);
      else await api.put(`/seasons/${seasonForm.data.id}`, seasonForm.data);
      toast.success("Stagione salvata"); setSeasonForm({ ...seasonForm, open: false }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const activateSeason = async (id) => {
    try { await api.post(`/seasons/${id}/activate`); toast.success("Stagione attivata"); reload(); }
    catch { toast.error("Errore"); }
  };
  const deleteSeason = async (id) => {
    if (!window.confirm("Eliminare la stagione? Lo storico degli atleti in questa stagione resta consultabile.")) return;
    await api.delete(`/seasons/${id}`); reload();
    if (selectedId === id) setSelectedId(null);
  };

  const doDuplicate = async () => {
    if (!duplicateFrom || !selectedId) return;
    if (duplicateFrom === selectedId) { toast.error("Seleziona una stagione diversa"); return; }
    try {
      const { data } = await api.post("/seasons/duplicate-rules", { from_season_id: duplicateFrom, to_season_id: selectedId });
      toast.success(`${data.duplicated} regole duplicate`); loadCategoriesAndRules();
    } catch (e) { toast.error("Errore duplicazione"); }
  };

  // Category CRUD
  const openNewCat = () => setCatForm({ open: true, mode: "new", data: { code: "", male_name: "", female_name: "", neutral_name: "", category_type: "age", sort_order: (categories.length + 1) * 10, is_active: true } });
  const openEditCat = (c) => setCatForm({ open: true, mode: "edit", data: { ...c } });
  const saveCat = async () => {
    try {
      const body = { ...catForm.data, code: catForm.data.code.toUpperCase().replace(/\s+/g, "_") };
      if (catForm.mode === "new") await api.post("/categories", body);
      else await api.put(`/categories/${body.id}`, body);
      toast.success("Categoria salvata"); setCatForm({ ...catForm, open: false }); loadCategoriesAndRules();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // Rule CRUD
  const openNewRule = () => setRuleForm({ open: true, mode: "new", data: { ...emptyRule, season_id: selectedId, category_id: categories[0]?.id || "" } });
  const openEditRule = (r) => setRuleForm({ open: true, mode: "edit", data: { ...r } });
  const saveRule = async () => {
    const d = { ...ruleForm.data };
    ["birth_year_from", "birth_year_to"].forEach((k) => { d[k] = d[k] === "" || d[k] === null ? null : parseInt(d[k]); });
    ["birth_date_from", "birth_date_to"].forEach((k) => { d[k] = d[k] || null; });
    try {
      if (ruleForm.mode === "new") await api.post("/season-rules", d);
      else await api.put(`/season-rules/${d.id}`, d);
      toast.success("Regola salvata"); setRuleForm({ ...ruleForm, open: false }); loadCategoriesAndRules();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const deleteRule = async (id) => {
    if (!window.confirm("Eliminare la regola?")) return;
    await api.delete(`/season-rules/${id}`); loadCategoriesAndRules();
  };
  const toggleRule = async (r) => {
    await api.put(`/season-rules/${r.id}`, { ...r, is_active: !r.is_active });
    loadCategoriesAndRules();
  };

  // Preview & recompute
  const doPreview = async () => {
    try { const { data } = await api.post(`/seasons/${selectedId}/recompute-all?preview=true`); setPreview(data); }
    catch { toast.error("Errore anteprima"); }
  };
  const doApply = async () => {
    if (!window.confirm(`Confermi il ricalcolo per ${preview?.total || 0} atleti (${preview?.with_changes || 0} variazioni)?`)) return;
    try {
      const { data } = await api.post(`/seasons/${selectedId}/recompute-all?preview=false`);
      toast.success(`Applicato: ${data.applied} categorie assegnate`);
      setPreview(null);
    } catch { toast.error("Errore applicazione"); }
  };

  const selectedSeason = seasons.find((s) => s.id === selectedId);

  if (user && user.role !== "admin") {
    return <Navigate to="/impostazioni" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Stagioni e categorie</h1>
          <p className="text-zinc-500 mt-1">
            <Link to="/impostazioni" className="hover:underline">← Impostazioni</Link> · Configurazione stagionale delle categorie, calcolo automatico e storico.
          </p>
        </div>
      </div>

      <Tabs defaultValue="seasons">
        <TabsList>
          <TabsTrigger value="seasons" data-testid="tab-seasons">Stagioni</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">Regole categorie</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Anagrafica categorie</TabsTrigger>
          <TabsTrigger value="recompute" data-testid="tab-recompute">Ricalcolo</TabsTrigger>
        </TabsList>

        {/* --- SEASONS --- */}
        <TabsContent value="seasons" className="pt-6">
          <div className="flex justify-end mb-3">
            <Button data-testid="new-season-btn" onClick={openNewSeason} className="bg-black hover:bg-zinc-800"><Plus className="w-4 h-4 mr-2" />Nuova stagione</Button>
          </div>
          <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <tr><th className="text-left px-5 py-3">Nome</th><th className="text-left px-5 py-3">Periodo</th><th className="text-left px-5 py-3">Stato</th><th className="w-56"></th></tr>
              </thead>
              <tbody>
                {seasons.map((s) => (
                  <tr key={s.id} className="border-b border-zinc-100" data-testid={`season-row-${s.id}`}>
                    <td className="px-5 py-3 font-medium">{s.name}</td>
                    <td className="px-5 py-3 font-mono text-zinc-600">{s.start_date} → {s.end_date}</td>
                    <td className="px-5 py-3">{s.is_active
                      ? <span className="text-[11px] uppercase px-2 py-1 bg-emerald-100 text-emerald-700 rounded">Attiva</span>
                      : <span className="text-[11px] uppercase px-2 py-1 bg-zinc-100 text-zinc-600 rounded">Inattiva</span>}
                    </td>
                    <td className="px-5 py-3 text-right space-x-1">
                      {!s.is_active && <Button data-testid={`activate-${s.id}`} size="sm" variant="outline" onClick={() => activateSeason(s.id)}><Play className="w-3.5 h-3.5 mr-1" />Attiva</Button>}
                      <Button size="sm" variant="outline" onClick={() => openEditSeason(s)}>Modifica</Button>
                      {!s.is_active && <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => deleteSeason(s.id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </td>
                  </tr>
                ))}
                {seasons.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-zinc-500">Nessuna stagione configurata.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* --- RULES --- */}
        <TabsContent value="rules" className="pt-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Stagione</Label>
              <Select value={selectedId || ""} onValueChange={setSelectedId}>
                <SelectTrigger data-testid="rules-season-select" className="w-56"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>{seasons.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_active ? " · attiva" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Select value={duplicateFrom} onValueChange={setDuplicateFrom}>
                <SelectTrigger data-testid="duplicate-from" className="w-48"><SelectValue placeholder="Duplica da…" /></SelectTrigger>
                <SelectContent>{seasons.filter((s) => s.id !== selectedId).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid="duplicate-rules-btn" variant="outline" onClick={doDuplicate} disabled={!duplicateFrom || !selectedId}><Copy className="w-4 h-4 mr-2" />Duplica regole</Button>
              <Button data-testid="new-rule-btn" onClick={openNewRule} disabled={!selectedId} className="bg-black hover:bg-zinc-800"><Plus className="w-4 h-4 mr-2" />Nuova regola</Button>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <tr>
                  <th className="text-left px-4 py-3">Categoria</th>
                  <th className="text-left px-4 py-3">Anni di nascita</th>
                  <th className="text-left px-4 py-3">Intervallo date</th>
                  <th className="text-left px-4 py-3">Tipo</th>
                  <th className="text-left px-4 py-3">Priorità</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="w-40"></th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => {
                  const c = catsById[r.category_id];
                  return (
                    <tr key={r.id} className="border-b border-zinc-100" data-testid={`rule-row-${r.id}`}>
                      <td className="px-4 py-3 font-medium">{c ? `${c.male_name} / ${c.female_name}` : r.category_id}<div className="text-xs text-zinc-500 font-mono">{c?.code}</div></td>
                      <td className="px-4 py-3 font-mono">{r.birth_year_from && r.birth_year_to ? `${r.birth_year_from}–${r.birth_year_to}` : "—"}</td>
                      <td className="px-4 py-3 font-mono">{r.birth_date_from || r.birth_date_to ? `${r.birth_date_from || "…"} → ${r.birth_date_to || "…"}` : "—"}</td>
                      <td className="px-4 py-3">
                        {r.is_primary
                          ? <span className="text-[11px] uppercase px-2 py-1 bg-black text-white rounded">Principale</span>
                          : <span className="text-[11px] uppercase px-2 py-1 bg-zinc-100 text-zinc-700 rounded">Aggiuntiva</span>}
                      </td>
                      <td className="px-4 py-3 font-mono">{r.priority}</td>
                      <td className="px-4 py-3">
                        <button data-testid={`toggle-rule-${r.id}`} onClick={() => toggleRule(r)} className={`text-[11px] uppercase px-2 py-1 rounded ${r.is_active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-500"}`}>
                          {r.is_active ? "Attiva" : "Inattiva"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => openEditRule(r)}>Modifica</Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => deleteRule(r.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  );
                })}
                {rules.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-zinc-500">Nessuna regola configurata.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* --- CATEGORIES --- */}
        <TabsContent value="categories" className="pt-6">
          <div className="flex justify-end mb-3">
            <Button data-testid="new-cat-btn" onClick={openNewCat} className="bg-black hover:bg-zinc-800"><Plus className="w-4 h-4 mr-2" />Nuova categoria</Button>
          </div>
          <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <tr><th className="text-left px-4 py-3">Codice</th><th className="text-left px-4 py-3">Maschile</th><th className="text-left px-4 py-3">Femminile</th><th className="text-left px-4 py-3">Tipo</th><th className="text-left px-4 py-3">Ordine</th><th className="w-32"></th></tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-100" data-testid={`cat-row-${c.code}`}>
                    <td className="px-4 py-3 font-mono">{c.code}</td>
                    <td className="px-4 py-3">{c.male_name}</td>
                    <td className="px-4 py-3">{c.female_name}</td>
                    <td className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500">{c.category_type}</td>
                    <td className="px-4 py-3 font-mono">{c.sort_order}</td>
                    <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => openEditCat(c)}>Modifica</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* --- RECOMPUTE --- */}
        <TabsContent value="recompute" className="pt-6 space-y-4">
          <div className="bg-white border border-zinc-200 rounded-md p-5 max-w-3xl space-y-3">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              <h3 className="font-heading font-semibold">Ricalcolo categorie per la stagione {selectedSeason?.name || "—"}</h3>
            </div>
            <p className="text-sm text-zinc-600">Anteprima le variazioni prima di applicarle. Lo storico delle stagioni precedenti resta invariato.</p>
            <div className="flex gap-2">
              <Button data-testid="preview-recompute" onClick={doPreview} disabled={!selectedId} variant="outline"><RefreshCw className="w-4 h-4 mr-2" />Anteprima variazioni</Button>
              {preview && preview.with_changes > 0 && <Button data-testid="apply-recompute" onClick={doApply} className="bg-black hover:bg-zinc-800"><Check className="w-4 h-4 mr-2" />Applica ({preview.with_changes})</Button>}
            </div>
            {preview && (
              <div className="pt-3 border-t border-zinc-200 space-y-2" data-testid="preview-summary">
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <Stat label="Totale atleti" value={preview.total} />
                  <Stat label="Con variazioni" value={preview.with_changes} highlight />
                  <Stat label="Senza data nascita" value={preview.no_birth_date} warn={preview.no_birth_date > 0} />
                  <Stat label="Senza regola valida" value={preview.no_rules_match} warn={preview.no_rules_match > 0} />
                </div>
                {preview.changes.length > 0 && (
                  <div className="mt-4 max-h-96 overflow-auto border border-zinc-200 rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-50 sticky top-0"><tr><th className="text-left px-3 py-2">Atleta</th><th className="text-left px-3 py-2">Data</th><th className="text-left px-3 py-2">Prima</th><th className="text-left px-3 py-2">Dopo</th></tr></thead>
                      <tbody>
                        {preview.changes.map((ch, i) => (
                          <tr key={i} className="border-b border-zinc-100">
                            <td className="px-3 py-2 font-medium">{ch.name}</td>
                            <td className="px-3 py-2 font-mono">{ch.birth_date}</td>
                            <td className="px-3 py-2 text-zinc-500">{(ch.before || []).map((b) => catsById[b.category_id]?.code || b.category_id).join(", ") || "—"}</td>
                            <td className="px-3 py-2">{(ch.after || []).map((a) => `${a.display_name}${a.is_primary ? "" : " (+)"}`).join(", ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {preview.no_birth_date + preview.no_rules_match > 0 && (
                  <div className="text-xs bg-amber-50 border border-amber-200 rounded p-3 flex gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-700 shrink-0" />
                    <span>{preview.no_birth_date} atleti senza data di nascita e {preview.no_rules_match} senza regola valida non verranno modificati.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Season dialog */}
      <Dialog open={seasonForm.open} onOpenChange={(o) => setSeasonForm({ ...seasonForm, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{seasonForm.mode === "new" ? "Nuova stagione" : "Modifica stagione"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Nome (es. 2027/2028)</Label><Input data-testid="season-name" value={seasonForm.data.name} onChange={(e) => setSeasonForm({ ...seasonForm, data: { ...seasonForm.data, name: e.target.value } })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data inizio</Label><Input type="date" data-testid="season-start" value={seasonForm.data.start_date} onChange={(e) => setSeasonForm({ ...seasonForm, data: { ...seasonForm.data, start_date: e.target.value } })} /></div>
              <div><Label>Data fine</Label><Input type="date" data-testid="season-end" value={seasonForm.data.end_date} onChange={(e) => setSeasonForm({ ...seasonForm, data: { ...seasonForm.data, end_date: e.target.value } })} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="season-save" onClick={saveSeason} className="bg-black hover:bg-zinc-800"><Save className="w-4 h-4 mr-2" />Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category dialog */}
      <Dialog open={catForm.open} onOpenChange={(o) => setCatForm({ ...catForm, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{catForm.mode === "new" ? "Nuova categoria" : "Modifica categoria"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Codice (stabile, es. RAGAZZI_2014)</Label><Input data-testid="cat-code" value={catForm.data.code} onChange={(e) => setCatForm({ ...catForm, data: { ...catForm.data, code: e.target.value } })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome maschile</Label><Input data-testid="cat-male" value={catForm.data.male_name} onChange={(e) => setCatForm({ ...catForm, data: { ...catForm.data, male_name: e.target.value } })} /></div>
              <div><Label>Nome femminile</Label><Input data-testid="cat-female" value={catForm.data.female_name} onChange={(e) => setCatForm({ ...catForm, data: { ...catForm.data, female_name: e.target.value } })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={catForm.data.category_type} onValueChange={(v) => setCatForm({ ...catForm, data: { ...catForm.data, category_type: v } })}>
                  <SelectTrigger data-testid="cat-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="age">Età</SelectItem><SelectItem value="master">Master</SelectItem><SelectItem value="special">Speciale</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Ordine</Label><Input type="number" value={catForm.data.sort_order} onChange={(e) => setCatForm({ ...catForm, data: { ...catForm.data, sort_order: parseInt(e.target.value || 0) } })} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="cat-save" onClick={saveCat} className="bg-black hover:bg-zinc-800"><Save className="w-4 h-4 mr-2" />Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rule dialog */}
      <Dialog open={ruleForm.open} onOpenChange={(o) => setRuleForm({ ...ruleForm, open: o })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{ruleForm.mode === "new" ? "Nuova regola" : "Modifica regola"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Categoria</Label>
              <Select value={ruleForm.data.category_id} onValueChange={(v) => setRuleForm({ ...ruleForm, data: { ...ruleForm.data, category_id: v } })}>
                <SelectTrigger data-testid="rule-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} — {c.male_name}/{c.female_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Anno nascita da</Label><Input type="number" data-testid="rule-yfrom" value={ruleForm.data.birth_year_from ?? ""} onChange={(e) => setRuleForm({ ...ruleForm, data: { ...ruleForm.data, birth_year_from: e.target.value } })} /></div>
              <div><Label>Anno nascita a</Label><Input type="number" data-testid="rule-yto" value={ruleForm.data.birth_year_to ?? ""} onChange={(e) => setRuleForm({ ...ruleForm, data: { ...ruleForm.data, birth_year_to: e.target.value } })} /></div>
            </div>
            <div className="text-xs text-zinc-500 -mt-2">— oppure —</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data da</Label><Input type="date" data-testid="rule-dfrom" value={ruleForm.data.birth_date_from || ""} onChange={(e) => setRuleForm({ ...ruleForm, data: { ...ruleForm.data, birth_date_from: e.target.value } })} /></div>
              <div><Label>Data a</Label><Input type="date" data-testid="rule-dto" value={ruleForm.data.birth_date_to || ""} onChange={(e) => setRuleForm({ ...ruleForm, data: { ...ruleForm.data, birth_date_to: e.target.value } })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label>Tipo</Label>
                <Select value={ruleForm.data.is_primary ? "primary" : "additional"} onValueChange={(v) => setRuleForm({ ...ruleForm, data: { ...ruleForm.data, is_primary: v === "primary" } })}>
                  <SelectTrigger data-testid="rule-primary"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="primary">Principale</SelectItem><SelectItem value="additional">Aggiuntiva ammessa</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Priorità</Label><Input type="number" data-testid="rule-priority" value={ruleForm.data.priority} onChange={(e) => setRuleForm({ ...ruleForm, data: { ...ruleForm.data, priority: parseInt(e.target.value || 0) } })} /></div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <input id="rule-active" type="checkbox" checked={!!ruleForm.data.is_active} onChange={(e) => setRuleForm({ ...ruleForm, data: { ...ruleForm.data, is_active: e.target.checked } })} />
              <Label htmlFor="rule-active" className="cursor-pointer">Regola attiva</Label>
            </div>
          </div>
          <DialogFooter><Button data-testid="rule-save" onClick={saveRule} className="bg-black hover:bg-zinc-800"><Save className="w-4 h-4 mr-2" />Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, highlight, warn }) {
  return (
    <div className={`border rounded p-2 ${highlight ? "border-black" : "border-zinc-200"} ${warn ? "bg-amber-50 border-amber-200" : ""}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-2xl font-heading font-bold ${highlight ? "text-black" : ""}`}>{value}</div>
    </div>
  );
}
